import { Globe, Lock, MoreHorizontal, Pencil, Power, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatPackSize } from "@/lib/materials/pack-size";
import { formatMoney } from "@/lib/materials/conversion-calc";
import { canEditMaterial, isSharedCatalogueMaterial } from "@/lib/materials/shared-catalogue";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { Material } from "@/lib/api/admin-api";

const TYPE_LABELS: Record<string, MessageDescriptor> = {
  RAW: msg`Raw`,
  COMPOUND: msg`Compound`,
  READY_TO_USE: msg`Ready to Use`,
};

interface MaterialTileProps {
  material: Material;
  /** Navigates to the ingredient detail page; the tile body is clickable. */
  onView?: () => void;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
  /** Only a super admin may write the shared catalogue — see `canEditMaterial`. */
  isSuperAdmin?: boolean;
}

export function MaterialTile({
  material,
  onView,
  onEdit,
  onToggle,
  onDelete,
  isSuperAdmin = false,
}: MaterialTileProps) {
  const { t } = useLingui();
  const imageSrc = material.thumbnailUrl ?? material.imageUrl;
  const canEdit = canEditMaterial(material, isSuperAdmin);

  return (
    <div
      role={onView ? "button" : undefined}
      tabIndex={onView ? 0 : undefined}
      onClick={
        onView
          ? (event) => {
              if ((event.target as HTMLElement).closest("button, a, [role='menuitem']")) return;
              onView();
            }
          : undefined
      }
      onKeyDown={
        onView
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onView();
              }
            }
          : undefined
      }
      className={`flex flex-col gap-3 rounded-xl border border-(--color-border) bg-(--color-surface-muted) p-4 w-full ${onView ? "cursor-pointer" : ""}`}
    >
      {/* Top row */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3.5 min-w-0">
          <div className="size-14 shrink-0 rounded-[10px] border border-(--color-border) bg-(--color-surface-content) flex items-center justify-center">
            {imageSrc ? (
              <img
                src={imageSrc}
                alt={material.name}
                className="size-full rounded-[10px] object-cover"
                loading="lazy"
              />
            ) : (
              <span className="text-sm font-semibold text-(--color-text-secondary)">{t`N/A`}</span>
            )}
          </div>

          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xl font-medium text-(--color-text-primary) leading-snug truncate">
                {material.name}
              </span>
              <Badge
                variant={material.isActive ? "success" : "secondary"}
                className={
                  material.isActive
                    ? "rounded-full border-transparent bg-(--color-status-active) text-(--brand-white) hover:bg-(--color-status-active)/90 shrink-0"
                    : "rounded-full shrink-0"
                }
              >
                {material.isActive ? t`Active` : t`Inactive`}
              </Badge>
              {/* Explains the missing action menu below: this row belongs to the
                  catalogue shared with every organization, not to this one. */}
              {isSharedCatalogueMaterial(material) && (
                <Badge variant="outline" className="gap-1 shrink-0">
                  <Lock className="size-3" />
                  {t`Shared catalogue`}
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-2 text-sm flex-wrap">
              {material.company ? (
                <>
                  <span className="text-(--color-text-secondary)">{t`Concept:`}</span>
                  <span className="text-(--color-text-primary)">{material.company.name}</span>
                </>
              ) : (
                <span className="inline-flex items-center gap-1 text-(--color-text-secondary)">
                  <Globe className="size-3" />
                  <Trans>Generic</Trans>
                </span>
              )}
              <span className="text-(--gray-300)">•</span>
              <span className="text-(--color-text-secondary)">{t`Unit:`}</span>
              <span className="text-(--color-text-primary)">{formatPackSize(material)}</span>
              {material.sku && (
                <>
                  <span className="text-(--gray-300)">•</span>
                  <span className="text-(--color-text-secondary)">{t`SKU:`}</span>
                  <span className="font-mono text-(--color-text-primary)">{material.sku}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {canEdit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="size-11 flex items-center justify-center rounded-[10px] border border-(--color-border) bg-(--color-surface-content) hover:bg-(--color-surface-accent) transition-colors"
                >
                  <MoreHorizontal className="size-5 text-(--color-text-secondary)" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem className="gap-2 cursor-pointer" onSelect={onEdit}>
                  <Pencil className="size-4" />
                  {t`Edit`}
                </DropdownMenuItem>
                <DropdownMenuItem className="gap-2 cursor-pointer" onSelect={onToggle}>
                  <Power className="size-4" />
                  {material.isActive ? t`Set as inactive` : t`Set as active`}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="gap-2 cursor-pointer text-(--color-danger-text) focus:text-(--color-danger-text)"
                  onSelect={onDelete}
                >
                  <Trash2 className="size-4" />
                  {t`Delete`}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Bottom: type + unit cost */}
      <div className="border-t border-(--color-border)" />
      <div className="flex items-center justify-between text-sm">
        <Badge variant="outline" className="font-normal">
          {TYPE_LABELS[material.type] ? t(TYPE_LABELS[material.type]!) : material.type}
        </Badge>
        {material.effectiveUnitCost != null && (
          <div className="flex items-center gap-2 text-(--color-text-secondary)">
            <span>{t`Unit cost:`}</span>
            <span className="font-mono text-(--color-text-primary)">
              {formatMoney(material.effectiveUnitCost.value)} / {material.effectiveUnitCost.unit}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
